/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import history from "../../../../@history";
import SectionModel from "../model/SectionModel";
import { showMessage } from "../../../store/BASE/messageSlice";

export const getSection = createAsyncThunk(
  "sections/getSection",
  async (id, { dispatch, getState }) => {
    try {
      // send request to get section
      const response = await axios.get(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/sections/${id}`
      );
      // get response data
      const data = await response.data;
      // return data
      return data;
    } catch (error) {
      // if error, redirect to sections page
      history.push({ pathname: `/sections` });
      // show error message
      dispatch(showMessage({ message: "Section Not Found", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const addSection = createAsyncThunk(
  "sections/addSection",
  async (section, { dispatch, getState }) => {
    try {
      // set user id
      section.user_id = getState().user.id;
      // send request to add section
      const response = await axios.post(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/sections`,
        section
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "Section Added" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "Section Not Added", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const updateSection = createAsyncThunk(
  "sections/updateSection",
  async (section, { dispatch, getState }) => {
    try {
      // set user id
      section.user_id = getState().user.id;
      // send request to update section
      const response = await axios.patch(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/sections/${section.id}`,
        section
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "Section Updated" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "Section Not Updated", variant: "error" }));
      // return null
      return null;
    }
  }
);

export const removeSection = createAsyncThunk(
  "sections/removeSection",
  async (id, { dispatch, getState }) => {
    try {
      // send request to remove section
      const response = await axios.delete(
        `${process.env.REACT_APP_API_SERVICE_URL}/api/sections/${id}`
      );
      // get response data
      const data = await response.data;
      // show success message
      dispatch(showMessage({ message: "Section Removed" }));
      // return data
      return data;
    } catch (error) {
      // show error message
      dispatch(showMessage({ message: "Section Not Removed", variant: "error" }));
      // return null
      return null;
    }
  }
);

// define selector
export const selectSection = ({ sections }) => sections.instance;

// define slice
const sectionSlice = createSlice({
  name: "section",
  initialState: null,
  reducers: {
    newSection: (state, action) => SectionModel(),
    resetSection: () => null,
  },
  extraReducers: {
    [getSection.pending]: (state, action) => null,
    [getSection.fulfilled]: (state, action) => action.payload,
    [updateSection.fulfilled]: (state, action) => action.payload,
    [removeSection.fulfilled]: (state, action) => null,
  },
});

// export actions
export const { resetSection, newSection } = sectionSlice.actions;

export default sectionSlice.reducer;
